import { useState } from "react";

// Plan can be either strings or objects with {id, task}
export type PlanItem = string | { id: number | string; task: string };

// Helper to extract text from plan item (handles both string and object formats)
export const getPlanItemText = (item: PlanItem): string => {
    if (typeof item === 'string') {
        return item;
    }
    return item.task;
};

// --- Change Tracking Types ---
// Each change in the stack represents an action taken on a step
export type StepChange = 
    | { type: 'edit'; previousText: string; newText: string }
    | { type: 'delete' };

export interface PlanStep {
    originalText: string;      // Original text from API (never changes)
    changes: StepChange[];     // Stack of changes (most recent at end)
}

// Derived state helpers
export const getStepCurrentText = (step: PlanStep): string => {
    // Apply all edit changes to get current text
    let text = step.originalText;
    for (const change of step.changes) {
        if (change.type === 'edit') {
            text = change.newText;
        }
    }
    return text;
};

export const isStepDeleted = (step: PlanStep): boolean => {
    // Step is deleted if the most recent change is a delete
    if (step.changes.length === 0) return false;
    return step.changes[step.changes.length - 1].type === 'delete';
};

export const isStepModified = (step: PlanStep): boolean => {
    return step.changes.length > 0;
};

// Generate command prefix from all pending changes
export const generateCommandPrefix = (steps: PlanStep[]): string => {
    const commands: string[] = [];
    
    steps.forEach((step, index) => {
        if (step.changes.length === 0) return;
        
        const stepNumber = index + 1;
        const isDeleted = isStepDeleted(step);
        const isNewStep = step.originalText === "";
        
        // Find the last edit before any delete
        let lastEditText = step.originalText;
        for (const change of step.changes) {
            if (change.type === 'edit') {
                lastEditText = change.newText;
            }
        }
        
        if (isDeleted) {
            if (isNewStep) {
                // New step that was deleted - no command needed
                return;
            }
            // If deleted, use originalText (what the LLM knows about)
            commands.push(`Remove step ${stepNumber}: "${step.originalText}"`);
        } else if (isNewStep) {
            // Newly added step
            commands.push(`Add step ${stepNumber}: "${lastEditText}"`);
        } else if (lastEditText !== step.originalText) {
            // If edited (and not deleted), show the edit
            commands.push(`Change step ${stepNumber} from "${step.originalText}" to "${lastEditText}"`);
        }
    });
    
    return commands.join('\n');
};

export function usePlanManagement(initialPlan: PlanItem[] = []) {
    const [planSteps, setPlanSteps] = useState<PlanStep[]>(
        initialPlan.map(item => ({ 
            originalText: getPlanItemText(item), 
            changes: [] 
        }))
    );
    const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
    const [editText, setEditText] = useState<string>("");

    // Initialize plan steps from external source
    const initializePlan = (plan: PlanItem[]) => {
        setPlanSteps(plan.map(item => ({ 
            originalText: getPlanItemText(item), 
            changes: [] 
        })));
    };

    // Start editing a step
    const startEditingStep = (index: number) => {
        const step = planSteps[index];
        if (isStepDeleted(step)) return;
        
        setEditingStepIndex(index);
        setEditText(getStepCurrentText(step));
    };

    // Save edit
    const saveEdit = () => {
        if (editingStepIndex === null) return;
        
        const step = planSteps[editingStepIndex];
        const currentText = getStepCurrentText(step);
        const newText = editText.trim();
        
        if (newText && newText !== currentText) {
            // Push an edit change onto the stack
            setPlanSteps(prev => prev.map((s, i) => 
                i === editingStepIndex 
                    ? { 
                        ...s, 
                        changes: [...s.changes, { type: 'edit', previousText: currentText, newText }] 
                    } 
                    : s
            ));
        }
        
        setEditingStepIndex(null);
        setEditText("");
    };

    // Cancel edit
    const cancelEdit = () => {
        setEditingStepIndex(null);
        setEditText("");
    };

    // Toggle step (delete/restore)
    const toggleStep = (index: number) => {
        const step = planSteps[index];
        const isNewStep = step.originalText === "";
        
        if (isStepDeleted(step)) {
            // Undo: pop the delete from the stack
            setPlanSteps(prev => prev.map((s, i) => 
                i === index 
                    ? { ...s, changes: s.changes.slice(0, -1) }  // Pop last change
                    : s
            ));
        } else if (isNewStep) {
            // New step: remove it completely from the array
            if (editingStepIndex === index) {
                setEditingStepIndex(null);
                setEditText("");
            } else if (editingStepIndex !== null && editingStepIndex > index) {
                // Adjust editing index if we're editing a step after the removed one
                setEditingStepIndex(editingStepIndex - 1);
            }
            setPlanSteps(prev => prev.filter((_, i) => i !== index));
        } else {
            // Original step: push a delete onto the stack
            setPlanSteps(prev => prev.map((s, i) => 
                i === index 
                    ? { ...s, changes: [...s.changes, { type: 'delete' }] }
                    : s
            ));
        }
    };

    // Revert a specific step to its original state
    const revertStepToOriginal = (index: number) => {
        const step = planSteps[index];
        const isNewStep = step.originalText === "";
        
        if (isNewStep) {
            // New step: remove it completely (no original to revert to)
            setEditingStepIndex(null);
            setEditText("");
            setPlanSteps(prev => prev.filter((_, i) => i !== index));
        } else {
            // Original step: clear all changes
            setPlanSteps(prev => prev.map((s, i) => 
                i === index ? { ...s, changes: [] } : s
            ));
            // Reset the edit text to original and stay in edit mode
            if (editingStepIndex === index) {
                setEditText(step.originalText);
            }
        }
    };

    // Reset all changes (restore original state)
    const resetAllChanges = () => {
        // Remove new steps (no original to revert to), clear changes for original steps
        setPlanSteps(prev => prev
            .filter(s => s.originalText !== "")  // Remove new steps
            .map(s => ({ ...s, changes: [] }))   // Clear changes for original steps
        );
        setEditingStepIndex(null);
        setEditText("");
    };

    // Add a new step at a specific index
    const addStep = (afterIndex: number) => {
        const newStep: PlanStep = {
            originalText: "",
            changes: [{ type: 'edit', previousText: "", newText: "New step" }]
        };
        setPlanSteps(prev => [
            ...prev.slice(0, afterIndex + 1),
            newStep,
            ...prev.slice(afterIndex + 1)
        ]);
        // Start editing the new step
        setEditingStepIndex(afterIndex + 1);
        setEditText("New step");
    };

    // Get final plan (non-deleted steps with their current text)
    const getFinalPlan = (): string[] => {
        return planSteps
            .filter(s => !isStepDeleted(s))
            .map(s => getStepCurrentText(s));
    };

    // Commit changes (update originalText and clear change stacks)
    const commitChanges = () => {
        setPlanSteps(prev => prev
            .filter(s => !isStepDeleted(s))
            .map(s => ({ 
                originalText: getStepCurrentText(s), 
                changes: [] 
            }))
        );
    };

    // Computed command prefix based on all pending changes
    const commandPrefix = generateCommandPrefix(planSteps);

    return {
        planSteps,
        editingStepIndex,
        editText,
        setEditText,
        commandPrefix,
        initializePlan,
        startEditingStep,
        saveEdit,
        cancelEdit,
        toggleStep,
        revertStepToOriginal,
        resetAllChanges,
        addStep,
        getFinalPlan,
        commitChanges,
    };
}
